// src/nexus/operationsPulse.js
//
// Operations-world extractors for the Pipeline Twin / Read-Path Twin cards in
// HqsCommandCenter's OperationsView. Mirrors the validate-or-null discipline
// established in systemPulse.js: a field is only trusted once (a) the owning
// endpoint's own classifyEndpointResult() state says "live"/"partial" and
// (b) the specific nested field has the expected type — otherwise null,
// never a guessed/fabricated value.
//
// Root cause this closes: PIPELINE_STAGES/"NO LIVE CAPACITY" in
// HqsCommandCenter.tsx rendered fixed placeholder text even though the
// "operations" world already fetches /api/admin/pipeline-status and
// /api/admin/db-capacity via LiveWorldPanel — the results just never reached
// OperationsView, they were only ever dumped as raw JSON in LiveWorldPanel's
// generic accordion.
//
// Verified against the real backend response shapes by reading the Railway
// route handlers directly (davidhuecker-bit/hqs-backend):
//   - GET /api/admin/pipeline-status -> routes/admin.routes.js ("/pipeline-status")
//       { success, stages: { [stageKey]: { inputCount, successCount,
//         failedCount, skippedCount, status, severity, dueCount, isCritical,
//         errorMessage, lastUpdated, source } }, generatedAt, lastRunMs }
//       stageKey is one of ALLOWED_STAGES_ARRAY in
//       services/pipelineStatus.repository.js.
//   - GET /api/admin/db-capacity -> services/dbCapacity.service.js#getDbCapacity
//       { success, tableSizes[], deadTuples[], indexUsage[], seqScanHotspots[],
//         retentionHints[], summary: { severity, attentionRequired,
//         operatorNotes[], largestTable, tablesNeedingVacuumCount,
//         unusedIndexCount, retentionRisk[], ... }, generatedAt, durationMs, errors[] }

const USABLE_ENDPOINT_STATES = new Set(["live", "partial"]);

function findEndpoint(endpointResults, id) {
  if (!Array.isArray(endpointResults)) return null;
  return endpointResults.find((entry) => entry && entry.id === id) || null;
}

function isUsable(endpoint) {
  return !!endpoint && USABLE_ENDPOINT_STATES.has(endpoint.state);
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isNonNegativeInteger(value) {
  return typeof value === "number" && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

function endpointBody(endpoint) {
  if (!endpoint) return null;
  const raw = endpoint.data;
  return isPlainObject(raw) ? raw : null;
}

function pushMissing(missingFields, field) {
  if (!missingFields.includes(field)) missingFields.push(field);
}

// Each frontend pipeline card (PIPELINE_STAGES in HqsCommandCenter.tsx) maps
// to one or more real backend stage keys (services/pipelineStatus.repository
// .js's ALLOWED_STAGES_ARRAY) — a "core" market-pipeline key and a
// "job-level" cron key can both describe the same conceptual step. The first
// key present in the live response's `stages` object wins.
export const PIPELINE_STAGE_KEY_CANDIDATES = Object.freeze({
  Prices: ["daily_price_ingestion", "universe", "massive_prices_healing"],
  World: ["snapshot_scan", "snapshot", "world_state_refresh"],
  Score: ["hqs_score_rebuild", "hqsScoring", "advanced_metrics_rebuild"],
  Forecast: ["forecast_input"],
  Verify: ["forecast_verification"],
  Outcome: ["outcome_tracking_update", "outcome"],
  Learn: ["forward_learning"],
  Capital: ["demo_portfolio_daily_run", "demo_portfolio_daily_runner", "ui_demo_portfolio"],
});

function extractStageEntry(stages, short) {
  if (!isPlainObject(stages)) return null;
  const candidates = PIPELINE_STAGE_KEY_CANDIDATES[short] || [];
  for (const key of candidates) {
    const entry = stages[key];
    if (isPlainObject(entry)) {
      return {
        stageKey: key,
        status: typeof entry.status === "string" ? entry.status : null,
        severity: typeof entry.severity === "string" ? entry.severity : null,
        inputCount: isNonNegativeInteger(entry.inputCount) ? entry.inputCount : null,
        successCount: isNonNegativeInteger(entry.successCount) ? entry.successCount : null,
        failedCount: isNonNegativeInteger(entry.failedCount) ? entry.failedCount : null,
        lastUpdated: typeof entry.lastUpdated === "string" ? entry.lastUpdated : null,
        isCritical: typeof entry.isCritical === "boolean" ? entry.isCritical : false,
      };
    }
  }
  return null;
}

export function extractPipelineStages(endpointResults, missingFields = []) {
  const endpoint = findEndpoint(endpointResults, "pipeline");
  const usable = isUsable(endpoint);
  if (!usable) pushMissing(missingFields, "pipelineStages");
  const body = usable ? endpointBody(endpoint) : null;
  const stages = body?.stages;
  const result = {};
  for (const short of Object.keys(PIPELINE_STAGE_KEY_CANDIDATES)) {
    result[short] = extractStageEntry(stages, short);
  }
  return {
    stages: result,
    generatedAt: typeof body?.generatedAt === "string" ? body.generatedAt : null,
  };
}

export function extractDbCapacitySummary(endpointResults, missingFields = []) {
  const endpoint = findEndpoint(endpointResults, "db-capacity");
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "dbCapacity");
    return null;
  }
  const body = endpointBody(endpoint);
  const summary = body?.summary;
  if (!isPlainObject(summary)) return null;
  return {
    severity: typeof summary.severity === "string" ? summary.severity : null,
    attentionRequired: typeof summary.attentionRequired === "boolean" ? summary.attentionRequired : null,
    tablesNeedingVacuumCount: isNonNegativeInteger(summary.tablesNeedingVacuumCount) ? summary.tablesNeedingVacuumCount : null,
    unusedIndexCount: isNonNegativeInteger(summary.unusedIndexCount) ? summary.unusedIndexCount : null,
    retentionRiskCount: Array.isArray(summary.retentionRisk) ? summary.retentionRisk.length : null,
    largestTablePretty: isPlainObject(summary.largestTable) && typeof summary.largestTable.totalSizePretty === "string" ? summary.largestTable.totalSizePretty : null,
  };
}

export function normalizeOperationsPulse({ endpointResults } = {}) {
  const missingFields = [];
  const pipeline = extractPipelineStages(endpointResults, missingFields);
  const dbCapacity = extractDbCapacitySummary(endpointResults, missingFields);
  return { pipeline, dbCapacity, missingFields };
}
